import React, { useState } from 'react';
import { Check, Pencil } from 'lucide-react';
import { SelectOption } from '../types/select-options';
import { SelectOptionColorPicker } from './SelectOptionColorPicker';
import { COLOR_MAPPINGS } from '../../../../utils/colorUtils';
import { createPortal } from 'react-dom';

export interface MobileSelectOptionItemProps {
  option: SelectOption;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: (newLabel: string) => void;
  onColorChange?: (newColor: string) => void;
}

export function MobileSelectOptionItem({
  option,
  isSelected,
  onSelect,
  onEdit,
  onColorChange,
}: MobileSelectOptionItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(option.label);
  // Create a local color state to preview color changes before saving
  const [previewColor, setPreviewColor] = useState(option.color);

  if (isEditing) {
    // Handle save changes
    const handleSave = () => {
      // Skip if nothing changed
      if (editValue.trim() === option.label && previewColor === option.color) {
        setIsEditing(false);
        return;
      }

      // Update label if changed
      if (editValue.trim() !== option.label) {
        onEdit(editValue);
      }

      // Update color if changed
      if (previewColor !== option.color && onColorChange) {
        onColorChange(previewColor);
      }

      setIsEditing(false);
    };

    // Handle cancel - reset to original values
    const handleCancel = () => {
      setEditValue(option.label);
      setPreviewColor(option.color);
      setIsEditing(false);
    };

    return (
      <div
        className="flex flex-col p-3 bg-white shadow-md rounded-md"
        onClick={e => e.stopPropagation()}
        // Prevent any clicks inside this container from closing the editor
        onTouchStart={e => e.stopPropagation()}
      >
        {/* Input row with color picker */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1">
            <input
              type="text"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              className="w-full px-3 py-2 text-base rounded bg-white border border-gray-200 focus:outline-none focus:border-blue-300 transition-all"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
                if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
            />
          </div>

          {/* Color picker outside the dropdown to make it appear above */}
          {onColorChange && (
            <div
              className="relative"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <div
                className={`w-8 h-8 rounded-full ${COLOR_MAPPINGS.dot[previewColor] || COLOR_MAPPINGS.dot.gray} cursor-pointer`}
                data-color-target={option.id}
                onClick={e => {
                  e.stopPropagation();
                  document.dispatchEvent(
                    new CustomEvent('open-color-picker-mobile', { detail: { id: option.id } })
                  );
                }}
              />

              {/* We'll add a separate color picker portal in the component */}
            </div>
          )}
        </div>

        {/* Button row */}
        <div className="flex justify-end gap-3">
          <button
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
            onClick={handleSave}
          >
            Save
          </button>
        </div>

        {/* External color picker that will appear through a portal */}
        {onColorChange &&
          createPortal(
            <SelectOptionColorPicker
              option={{ ...option, color: previewColor }}
              onColorSelect={color => {
                setPreviewColor(color);
              }}
              position="right"
              targetId={option.id}
              isMobile={true}
            />,
            document.body
          )}
      </div>
    );
  }

  const { color } = option;

  return (
    <div className="w-full flex items-center justify-between hover:bg-gray-100 px-4 py-2">
      <button className="flex-1 text-left flex items-center" onClick={onSelect}>
        <div className="flex items-center gap-2">
          <div
            className={`w-4 h-4 rounded-full ${COLOR_MAPPINGS.bg[color] || COLOR_MAPPINGS.bg.gray} flex items-center justify-center`}
          >
            {isSelected && <Check size={10} className="text-white" />}
          </div>
          <span>{option.label}</span>
        </div>
      </button>

      <div className="flex items-center space-x-1">
        <button
          className="text-gray-400 p-2"
          onClick={e => {
            e.stopPropagation();
            setIsEditing(true);
          }}
        >
          <Pencil size={16} />
        </button>
      </div>
    </div>
  );
}
